import type {
  GameDebrief,
  GameDebriefInput,
  GameDifficulty,
  GameDirectorDecision,
  GameDirectorObservation,
  GamePressure,
  GameRunDefinition,
  GameRunType,
  GameScoreInput,
  GameTargetCandidate,
  GameTargetResolution,
  GameWaveDefinition,
  GameWordSelection,
  LogicalPoint,
} from "./types";

export const GAME_SCORING_VERSION = "game-score-2.0.0";
export const GAME_DIRECTOR_VERSION = "game-director-1.0.0";

const RUN_DEFINITIONS: Record<GameRunType, GameRunDefinition> = {
  guided: {
    runType: "guided",
    label: "Guided Training",
    description: "Practice one focus under responsive, accuracy-first pressure.",
    targetAccuracy: 96,
    targetDurationMs: 3 * 60 * 1_000,
    canGameOver: true,
    focusPatternShare: 0.6,
  },
  arcade: {
    runType: "arcade",
    label: "Arcade Run",
    description: "Climb open-ended waves and protect the core for a high score.",
    targetAccuracy: 94,
    targetDurationMs: null,
    canGameOver: true,
    focusPatternShare: 0.2,
  },
  warmup: {
    runType: "warmup",
    label: "Warm-Up",
    description: "Build a clean rhythm for two minutes without a game over.",
    targetAccuracy: 97,
    targetDurationMs: 2 * 60 * 1_000,
    canGameOver: false,
    focusPatternShare: 0.35,
  },
};

const DIFFICULTY_FACTORS: Record<
  GameDifficulty,
  {
    spawn: number;
    movement: number;
    active: number;
    chain: number;
    shield: number;
  }
> = {
  "super-easy": {
    spawn: 1.55,
    movement: 0.58,
    active: 0.55,
    chain: 0.25,
    shield: 0,
  },
  easy: {
    spawn: 1.25,
    movement: 0.78,
    active: 0.75,
    chain: 0.6,
    shield: 0.4,
  },
  normal: {
    spawn: 1,
    movement: 1,
    active: 1,
    chain: 1,
    shield: 1,
  },
  difficult: {
    spawn: 0.84,
    movement: 1.13,
    active: 1.15,
    chain: 1.25,
    shield: 1.25,
  },
  impossible: {
    spawn: 0.72,
    movement: 1.28,
    active: 1.3,
    chain: 1.5,
    shield: 1.5,
  },
};

function clamp(value: number, minimum: number, maximum: number): number {
  return Math.min(maximum, Math.max(minimum, value));
}

function round(value: number, places = 4): number {
  const factor = 10 ** places;
  return Math.round(value * factor) / factor;
}

export function getGameRunDefinition(runType: GameRunType): GameRunDefinition {
  return { ...RUN_DEFINITIONS[runType] };
}

export function normalizeGameRunType(value: string | null): GameRunType {
  if (value === "guided" || value === "warmup") {
    return value;
  }
  return "arcade";
}

export function gameRunMeetsEvidenceThreshold(
  activeDurationMs: number,
  printableAttempts: number,
): boolean {
  return activeDurationMs >= 120_000 || printableAttempts >= 100;
}

export function createInitialGamePressure(
  runType: GameRunType,
  difficulty: GameDifficulty,
): GamePressure {
  const run = RUN_DEFINITIONS[runType];
  const factor = DIFFICULTY_FACTORS[difficulty];
  const runSpawnFactor =
    runType === "warmup" ? 1.35 : runType === "guided" ? 1.12 : 1;
  const runMovementFactor =
    runType === "warmup" ? 0.7 : runType === "guided" ? 0.88 : 1;

  return {
    spawnIntervalMs: Math.round(
      2_000 * factor.spawn * runSpawnFactor,
    ),
    movementUnitsPerSecond: round(
      0.029 * factor.movement * runMovementFactor,
    ),
    maxActiveEnemies: Math.max(
      3,
      Math.round(10 * factor.active * (runType === "warmup" ? 0.72 : 1)),
    ),
    chainChance: round(
      0.1 * factor.chain * (runType === "warmup" ? 0.3 : 1),
    ),
    shieldChance: round(
      0.08 * factor.shield * (runType === "warmup" ? 0 : 1),
    ),
    specialChance: runType === "arcade" ? 0.04 : 0.02,
    focusPatternShare: run.focusPatternShare,
  };
}

export function createGameWaveDefinition(
  runType: GameRunType,
  difficulty: GameDifficulty,
  wave: number,
  pressureOverride?: GamePressure,
): GameWaveDefinition {
  const safeWave = Math.max(1, Math.floor(wave));
  const base = pressureOverride
    ? { ...pressureOverride }
    : createInitialGamePressure(runType, difficulty);
  const progression = Math.min(0.36, (safeWave - 1) * 0.025);
  const pressure = {
    ...base,
    spawnIntervalMs: Math.round(
      clamp(base.spawnIntervalMs * (1 - progression), 650, 5_000),
    ),
    movementUnitsPerSecond: round(
      clamp(base.movementUnitsPerSecond * (1 + progression * 0.7), 0.009, 0.08),
    ),
    maxActiveEnemies: Math.round(
      clamp(base.maxActiveEnemies + Math.floor((safeWave - 1) / 2), 3, 24),
    ),
    chainChance: round(
      clamp(base.chainChance + Math.max(0, safeWave - 4) * 0.012, 0, 0.42),
    ),
    shieldChance: round(
      clamp(base.shieldChance + Math.max(0, safeWave - 7) * 0.01, 0, 0.32),
    ),
  };

  return {
    wave: safeWave,
    wordsRequired: 10 + (safeWave - 1) * 2,
    pressure,
    threatTierCeiling:
      safeWave >= 16 ? 4 : safeWave >= 10 ? 3 : safeWave >= 5 ? 2 : 1,
  };
}

export function adjustGamePressure(
  current: GamePressure,
  observation: GameDirectorObservation,
  targetAccuracy: number,
): GameDirectorDecision {
  const accuracy = observation.accuracy;
  const performanceStrained =
    (accuracy !== null && accuracy < targetAccuracy - 3) ||
    observation.errorBurstRate >= 0.16 ||
    (observation.focusAccuracy !== null && observation.focusAccuracy < 80);
  const integrityEmergency =
    observation.coreIntegrity <= 35 || observation.recentDamage >= 20;
  const frameStrained =
    observation.averageFrameMs !== null && observation.averageFrameMs > 24;

  if (integrityEmergency || performanceStrained) {
    return {
      pressure: {
        ...current,
        spawnIntervalMs: Math.round(
          clamp(current.spawnIntervalMs * (integrityEmergency ? 1.2 : 1.12), 650, 5_000),
        ),
        movementUnitsPerSecond: round(
          clamp(
            current.movementUnitsPerSecond * (integrityEmergency ? 0.86 : 0.92),
            0.009,
            0.08,
          ),
        ),
        maxActiveEnemies: Math.max(
          3,
          current.maxActiveEnemies - (integrityEmergency ? 2 : 1),
        ),
      },
      direction: "eased",
      reason: integrityEmergency
        ? "Core integrity needs recovery room."
        : "Accuracy pressure moved below the run target.",
    };
  }

  if (frameStrained) {
    return {
      pressure: {
        ...current,
        specialChance: round(Math.max(0, current.specialChance * 0.5)),
      },
      direction: "held",
      reason: "Mechanical pressure held while visual load is reduced.",
    };
  }

  const accuracySupportsIncrease =
    accuracy !== null &&
    accuracy >= targetAccuracy &&
    observation.activeEnemyCount <= Math.max(1, current.maxActiveEnemies - 2) &&
    observation.recentDamage === 0;
  if (accuracySupportsIncrease) {
    return {
      pressure: {
        ...current,
        spawnIntervalMs: Math.round(
          clamp(current.spawnIntervalMs * 0.93, 650, 5_000),
        ),
        movementUnitsPerSecond: round(
          clamp(current.movementUnitsPerSecond * 1.045, 0.009, 0.08),
        ),
        maxActiveEnemies: Math.round(
          clamp(current.maxActiveEnemies + 1, 3, 24),
        ),
      },
      direction: "increased",
      reason: "Clean typing supports a little more pressure.",
    };
  }

  return {
    pressure: { ...current },
    direction: "held",
    reason: "Current pressure matches the available evidence.",
  };
}

export function clampLogicalPoint(
  point: LogicalPoint,
  inset = 0,
): LogicalPoint {
  return {
    x: round(clamp(point.x, inset, 1 - inset)),
    y: round(clamp(point.y, inset, 1 - inset)),
  };
}

export function designPointToLogical(
  point: LogicalPoint,
  designSize = 1_000,
): LogicalPoint {
  return clampLogicalPoint({
    x: point.x / designSize,
    y: point.y / designSize,
  });
}

export function logicalPointToDesign(
  point: LogicalPoint,
  designSize = 1_000,
): LogicalPoint {
  const safe = clampLogicalPoint(point);
  return {
    x: round(safe.x * designSize, 2),
    y: round(safe.y * designSize, 2),
  };
}

export function logicalDistanceToCore(
  point: LogicalPoint,
  core: LogicalPoint = { x: 0.5, y: 0.5 },
): number {
  return Math.hypot(point.x - core.x, point.y - core.y);
}

export function advanceLogicalPointTowardCore(
  point: LogicalPoint,
  unitsPerSecond: number,
  deltaMs: number,
  core: LogicalPoint = { x: 0.5, y: 0.5 },
): LogicalPoint {
  const dx = core.x - point.x;
  const dy = core.y - point.y;
  const distance = Math.hypot(dx, dy);
  if (distance === 0 || deltaMs <= 0 || unitsPerSecond <= 0) {
    return clampLogicalPoint(point);
  }
  const travel = Math.min(distance, unitsPerSecond * (deltaMs / 1_000));
  return clampLogicalPoint({
    x: point.x + (dx / distance) * travel,
    y: point.y + (dy / distance) * travel,
  });
}

function compareTargetPriority(
  left: GameTargetCandidate,
  right: GameTargetCandidate,
): number {
  return (
    right.threatTier - left.threatTier ||
    left.distanceToCore - right.distanceToCore ||
    left.spawnedAtMs - right.spawnedAtMs ||
    String(left.id).localeCompare(String(right.id))
  );
}

export function resolveGameTarget(
  candidates: readonly GameTargetCandidate[],
  typedPrefix: string,
  options: {
    caseSensitive?: boolean;
    explicitlyLockedTargetId?: string | number | null;
  } = {},
): GameTargetResolution {
  const prefix = options.caseSensitive ? typedPrefix : typedPrefix.toLowerCase();
  const eligible = candidates.filter((candidate) => !candidate.chainLocked);
  const normalized = (value: string) =>
    options.caseSensitive ? value : value.toLowerCase();
  const matches = eligible
    .filter((candidate) => normalized(candidate.displayedText).startsWith(prefix))
    .sort(compareTargetPriority);
  const exact = matches.filter(
    (candidate) => normalized(candidate.displayedText) === prefix,
  );
  const explicitlyLocked = matches.find(
    (candidate) => candidate.id === options.explicitlyLockedTargetId,
  );
  const leading = explicitlyLocked ?? matches[0] ?? null;
  const locked =
    explicitlyLocked ?? (matches.length === 1 ? matches[0] : null) ?? null;
  const exactTarget =
    exact.find(
      (candidate) => candidate.id === options.explicitlyLockedTargetId,
    ) ??
    exact[0] ??
    null;

  return {
    matchingTargetIds: matches.map((candidate) => candidate.id),
    leadingTargetId: leading?.id ?? null,
    lockedTargetId: locked?.id ?? null,
    exactTargetId: exactTarget?.id ?? null,
  };
}

function containsFocus(word: string, patterns: readonly string[]): boolean {
  const normalizedWord = word.toLowerCase();
  return patterns.some((pattern) => {
    const token = pattern.toLowerCase().replace(/^[^:]+:/, "");
    return token.length > 0 && normalizedWord.includes(token);
  });
}

export function selectGameWord(options: {
  words: readonly string[];
  focusPatterns?: readonly string[];
  focusPatternShare: number;
  randomValue: number;
  recentWords?: readonly string[];
}): GameWordSelection {
  const recent = new Set(
    (options.recentWords ?? []).map((word) => word.toLowerCase()),
  );
  const available = options.words.filter(
    (word) => word.length > 0 && !recent.has(word.toLowerCase()),
  );
  const pool = available.length > 0 ? available : [...options.words];
  const focusPatterns = options.focusPatterns ?? [];
  const focusWords = pool.filter((word) => containsFocus(word, focusPatterns));
  const shouldFocus =
    focusWords.length > 0 &&
    clamp(options.randomValue, 0, 0.999999) < options.focusPatternShare;
  const chosenPool = shouldFocus ? focusWords : pool;
  const normalizedRandom = clamp(options.randomValue * 997 % 1, 0, 0.999999);
  const word =
    chosenPool[Math.floor(normalizedRandom * chosenPool.length)] ??
    options.words[0] ??
    "";
  const matchesFocus = containsFocus(word, focusPatterns);

  return {
    word,
    reason: shouldFocus ? "focus" : matchesFocus ? "review" : "support",
    matchesFocus,
  };
}

export function calculateGameScore(input: GameScoreInput): number {
  const base = Math.max(10, Math.max(1, input.wordLength) * 10);
  const dangerMultiplier = 1 + clamp(input.threatTier - 1, 0, 3) * 0.16;
  const chainMultiplier = input.isChain ? 1.5 : 1;
  const accuracyMultiplier = input.firstAttemptCorrect ? 1.2 : 0.65;
  const momentumMultiplier = 1 + clamp(input.momentum, 0, 20) * 0.08;
  return Math.round(
    base *
      dangerMultiplier *
      chainMultiplier *
      accuracyMultiplier *
      momentumMultiplier,
  );
}

export function coreDamageForThreat(threatTier: number): number {
  return [10, 15, 20, 25][clamp(Math.round(threatTier), 1, 4) - 1] ?? 10;
}

export function buildGameDebrief(input: GameDebriefInput): GameDebrief {
  const headline =
    input.accuracy === null && input.accurateWpm === null
      ? "More measured typing is needed to establish pace and accuracy."
      : input.accuracy === null
        ? `Accuracy is still provisional at ${Math.round(input.accurateWpm ?? 0)} accurate WPM.`
        : input.accurateWpm === null
          ? `${Math.round(input.accuracy)}% accuracy; pace is still provisional.`
          : `${Math.round(input.accuracy)}% accuracy at ${Math.round(input.accurateWpm)} accurate WPM.`;
  const focusAccuracy =
    input.focusAttempts > 0
      ? (input.focusCorrectAttempts / input.focusAttempts) * 100
      : null;
  const focusSummary =
    input.focusLabel && focusAccuracy !== null
      ? `${input.focusLabel}: ${Math.round(focusAccuracy)}% across ${input.focusAttempts} focus attempts.`
      : input.focusLabel
        ? `${input.focusLabel}: complete more focus words to establish evidence.`
        : "This run used a broad fluency mix rather than one prescribed focus.";
  const outcome =
    input.runType === "warmup"
      ? "Warm-up complete"
      : input.coreIntegrity > 0
        ? "Mission complete"
        : "Core breached";
  const validCopy = input.valid
    ? "The run met the evidence threshold and updated shared mastery."
    : "The run remains useful practice, but it did not update shared mastery.";
  const increased = input.pressureDirectionCounts.increased ?? 0;
  const eased = input.pressureDirectionCounts.eased ?? 0;

  return {
    outcome,
    headline,
    evidence: [
      `${input.wordsCompleted} words cleared; ${input.wordsEscaped} reached the core.`,
      `Best clean momentum: ${input.maxCombo}. Core integrity: ${Math.round(input.coreIntegrity)}%.`,
      `The director increased pressure ${increased} time${increased === 1 ? "" : "s"} and eased it ${eased} time${eased === 1 ? "" : "s"}.`,
      validCopy,
    ],
    focusSummary,
    nextAction:
      !input.valid
        ? "Complete either two active minutes or 100 printable attempts to establish comparable evidence."
        : input.accuracy !== null && input.accuracy < 94
        ? "Choose Relaxed pressure and rebuild a clean combo."
        : focusAccuracy !== null && focusAccuracy < 90
          ? "Repeat the Guided run once more with the same focus."
          : "Advance one pressure step or return to Professional Training for a focused follow-up.",
  };
}
